import { Injectable, inject } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { FormServiceProxy } from 'app/shared/services/form-service.proxy';
import { CalculateAllResult } from 'app/shared/services/model/form-service.model';
import { ApplicationHelperService } from 'app/modules/forms/application-helper.service';
import { iApplyFormControl } from 'app/shared/model';
import _ from 'lodash';
import { hasValue } from 'app/shared';
import { configDefault } from 'app/shared/app-defaults';

interface CalculationRequest {
    appId: string,
    pageId: number,
    subject: Subject<CalculateAllResult | never | null>,
    executeAndSetValuesSubscription: Subscription | undefined,
    timerId: ReturnType<typeof setTimeout> | undefined
}

@Injectable()
export class CalculationService {
    private _formServiceProxy = inject(FormServiceProxy);
    private _applicationHelperService = inject(ApplicationHelperService);

    private calculateAllTimeout = configDefault.timings.calculateAllTimeout || 300;
    private calculations: CalculationRequest[] = [];

    getCalculationObservable(appId: string, pageId: number) {
        return this.getCalculationRequest(appId, pageId).subject.asObservable();
    }

    private getCalculationRequest(appId: string, pageId: number): CalculationRequest {
        let calc = this.calculations.find(c => c.appId == appId && c.pageId == pageId);
        if (!calc) {
            calc = <CalculationRequest>{
                appId: appId,
                pageId: pageId,
                subject: new Subject<CalculateAllResult | never | null>(),
                executeAndSetValuesSubscription: undefined,
                timerId: undefined
            };

            this.calculations.push(calc);
        }

        return calc;
    }

    calculateAll(
        formId: string,
        formVersion: number,
        useFormVersion: boolean,
        appId: string,
        appData: any,
        pageId: number
    ): Observable<CalculateAllResult | null> {
        const calc = this.getCalculationRequest(appId, pageId);

        clearTimeout(calc.timerId);

        calc.timerId = setTimeout(() => {
            let sb: Subscription | null = null; // for tests
            sb = this._formServiceProxy.calculateAll(formId, formVersion, useFormVersion, appId, appData, pageId).subscribe({
                next: (x: CalculateAllResult | null) => {
                    if (x != null) {
                        x['pageId'] = pageId;
                    }
                    calc.subject.next(x);
                },
                complete: () => {
                    if (sb && !sb.closed) {
                        sb.unsubscribe();
                    }
                }
            });
        }, this.calculateAllTimeout);

        return calc.subject.asObservable();
    }

    executeAndSetValues(
        formId: string,
        formVersion: number,
        useFormVersion: boolean,
        appId: string,
        appData: any,
        pageId: number,
        controls: iApplyFormControl[]
    ) {
        if (!hasValue(formId) || !hasValue(appId)) {
            return;
        }

        // this.calculateAll() requests are unique per appId + pageId.
        // Previous subscriptions to executeAndSetValues for the SAME appId/pageId should be
        // ignored/unsubscribed, and a new subscription created for the latest request.
        // Only the latest request should complete.

        const calc = this.getCalculationRequest(appId, pageId);
        if (calc.executeAndSetValuesSubscription && !calc.executeAndSetValuesSubscription.closed) {
            calc.executeAndSetValuesSubscription.unsubscribe();
        }

        // TODO: NavigationService.preventNavigation('CalculationService', ['next', 'submit']);
        calc.executeAndSetValuesSubscription = this.calculateAll(formId, formVersion, useFormVersion, appId, appData, pageId)
            .subscribe({
                next: (response) => {
                    this.onCalculateAllSuccess(response, appData);
                    calc.executeAndSetValuesSubscription = undefined;
                    // TODO: NavigationService.allowNavigation('CalculationService', ['next', 'submit']);  //ToDo
                    // TODO: $rootScope.$emit('Calculations-complete');  //ToDo
                },
                error: (response) => {
                    this.onCalculateAllError(response, appData, controls);
                    calc.executeAndSetValuesSubscription = undefined;
                    // TODO: NavigationService.allowNavigation('CalculationService', ['next', 'submit']);  //ToDo
                }
            });
    };

    private onCalculateAllSuccess(response: CalculateAllResult | null, appData: any) {
        if (response?.success) {
            let calcResults = response.result;
            for (let key in calcResults) {
                if (Object.hasOwn(calcResults, key)) {
                    this._applicationHelperService.setFieldValue(appData, key, calcResults[key], true);
                }
            }
        }
    }

    private onCalculateAllError(response, appData, controls) {
        console.error(response);
        if (response && response.cancelled === true) {
            return;
        }

        // if (response && response.data && response.data.disable === true) {
        //     // TODO: This doesn't handle repeaters/groups in the control list or application data...
        //     let calculationControls = _.filter(controls, ['Type', 'calculation']); //ToDo replace
        //     for (var i = 0; i < calculationControls.length; i++) {
        //         var control = calculationControls[i];
        //         if ({}.hasOwnProperty.call(application, control.Name)) {
        //             this._applicationHelperService.setFieldValue(application, control.Name, "Calculations are disabled");
        //         }
        //     }
        // }
    }
}
